import React, { useEffect, useState } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { Building2, Save } from 'lucide-react';
import { organizationsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const BUSINESS_TYPES = ['restaurant', 'hotel', 'property_management', 'automotive'];

export default function OrganizationSettings() {
    const { t } = useTranslation();
    const { isSuperAdmin, isClientAdmin } = useAuth();
    const [orgs, setOrgs] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [saving, setSaving] = useState({});

    const load = async () => {
        try {
            const res = await organizationsAPI.list();
            const list = res.data || [];
            setOrgs(sortByName(list));
            setDrafts(Object.fromEntries(list.map((o) => [o.id, o.vertical || ''])));
        } catch {
            toast.error(t('orgSettings.loadFailed'));
            setOrgs([]);
        }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const save = async (org) => {
        const vertical = drafts[org.id];
        if (!vertical) return;
        setSaving((s) => ({ ...s, [org.id]: true }));
        try {
            await organizationsAPI.setVertical(org.id, vertical);
            setOrgs((prev) => prev.map((o) => (o.id === org.id ? { ...o, vertical } : o)));
            toast.success(t('orgSettings.saved'));
        } catch (e) {
            toast.error(e.response?.data?.detail || t('orgSettings.saveFailed'));
        } finally {
            setSaving((s) => ({ ...s, [org.id]: false }));
        }
    };

    if (!isSuperAdmin() && !isClientAdmin()) {
        return (
            <div className="p-8" data-testid="org-settings-denied">
                <p className="text-muted-foreground">{t('orgSettings.denied')}</p>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="org-settings-page">
            <div>
                <h1 className="text-2xl font-semibold flex items-center gap-2">
                    <Building2 className="w-6 h-6 text-rose-500" />
                    {t('orgSettings.title')}
                </h1>
                <p className="text-muted-foreground mt-1 text-sm">{t('orgSettings.subtitle')}</p>
            </div>

            {orgs === null ? (
                <p className="text-muted-foreground text-sm">{t('orgSettings.loading')}</p>
            ) : orgs.length === 0 ? (
                <p className="text-muted-foreground text-sm">{t('orgSettings.none')}</p>
            ) : (
                orgs.map((org) => {
                    const draft = drafts[org.id] || '';
                    const dirty = draft && draft !== (org.vertical || '');
                    return (
                        <Card key={org.id} data-testid={`org-settings-row-${org.id}`}>
                            <CardHeader>
                                <CardTitle className="text-lg">{org.name}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <label className="block text-sm font-medium">{t('orgSettings.businessType')}</label>
                                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                                    <Select
                                        value={draft}
                                        onValueChange={(v) => setDrafts((d) => ({ ...d, [org.id]: v }))}
                                    >
                                        <SelectTrigger className="sm:w-72" data-testid={`org-business-type-select-${org.id}`}>
                                            <SelectValue placeholder={t('orgSettings.notSet')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {BUSINESS_TYPES.map((bt) => (
                                                <SelectItem key={bt} value={bt}>
                                                    {t(`clients.businessType${bt === 'property_management' ? 'PropertyManagement' : bt.charAt(0).toUpperCase() + bt.slice(1)}`)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button
                                        onClick={() => save(org)}
                                        disabled={!dirty || saving[org.id]}
                                        data-testid={`org-business-type-save-${org.id}`}
                                    >
                                        <Save className="w-4 h-4 mr-2" />
                                        {saving[org.id] ? t('orgSettings.saving') : t('orgSettings.save')}
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {org.vertical ? t('orgSettings.current', {
                                        value: t(`clients.businessType${org.vertical === 'property_management' ? 'PropertyManagement' : org.vertical.charAt(0).toUpperCase() + org.vertical.slice(1)}`),
                                    }) : t('orgSettings.autoHint')}
                                </p>
                            </CardContent>
                        </Card>
                    );
                })
            )}
        </div>
    );
}
